import { normalizePalette, type ProfilePalette, type PublicProfileCard } from './profile-link-card';
import { profileDisplayUrl } from './profile-link-view';
import {
  PROFILE_LINK_ACTIONS,
  PROFILE_LINK_LIMITS,
  type ProfileLead,
  type ProfileLink,
  type ProfileLinkAction,
  type ProfileLinkPatch,
  type ProfileLinkStats,
  type ProfileLinkStatus,
} from './profile-links-types';

/**
 * The pure decisions behind the owner screens (`/profile-links`, `/profile-links/<id>`): how the list is
 * grouped and ordered, what each status says, what the editor's draft is, which edits become a patch, what a
 * visitor would see, and the share / leads lines. No React, no fetch — every rule is unit-tested
 * (test/profile-links-model.test.ts), and iOS/Android implement the same rules under the same names.
 *
 * docs/symphony-profile-links-design.md §7.1 (the entity), §8.2 (screens), §8.3 (leads), Q2 (the cap).
 */

// ---- the list ----

export interface ProfileLinksSections {
  /** The main profile — the one link with no context. Absent until the account has one. */
  main?: ProfileLink;
  /** Live first, then paused, then drafts; within a status, by title. Archived links are not listed. */
  contexts: ProfileLink[];
}

const STATUS_ORDER: Record<ProfileLinkStatus, number> = { ACTIVE: 0, PAUSED: 1, DRAFT: 2, ARCHIVED: 3 };

export function profileLinksSections(links: readonly ProfileLink[]): ProfileLinksSections {
  const main = links.find((link) => !link.contextSlug && link.status !== 'ARCHIVED');
  const contexts = links
    .filter((link) => link.contextSlug && link.status !== 'ARCHIVED')
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || linkTitleSortKey(a).localeCompare(linkTitleSortKey(b)),
    );
  return { main, contexts };
}

const linkTitleSortKey = (link: ProfileLink) => (link.contextTitle || link.contextSlug || '').toLowerCase();

/** The status pill: its i18n key and the DS badge it wears. */
export function statusPill(status: ProfileLinkStatus): { key: string; variant: 'active' | 'paused' | 'waiting-on-you' | 'done' } {
  switch (status) {
    case 'ACTIVE':
      return { key: 'profileLinks.status.live', variant: 'active' };
    case 'PAUSED':
      return { key: 'profileLinks.status.paused', variant: 'paused' };
    case 'DRAFT':
      return { key: 'profileLinks.status.draft', variant: 'waiting-on-you' };
    default:
      return { key: 'profileLinks.status.archived', variant: 'done' };
  }
}

/** The one primary lifecycle action a link offers: publish a draft, pause a live link, resume a paused one. */
export function lifecycleAction(status: ProfileLinkStatus): 'publish' | 'pause' | 'resume' | null {
  if (status === 'DRAFT') return 'publish';
  if (status === 'ACTIVE') return 'pause';
  if (status === 'PAUSED') return 'resume';
  return null;
}

/** The printed address of a link (`symphony.wix.app/me/<h>[/<c>]`). */
export function linkAddress(link: Pick<ProfileLink, 'handle' | 'contextSlug'>): string {
  return profileDisplayUrl(link.handle, link.contextSlug);
}

/** "People" is contacts; "taps" folds the two CTAs together (the mock's four tiles). */
export function statTiles(stats: ProfileLinkStats): { key: string; value: number }[] {
  return [
    { key: 'profileLinks.stats.views', value: stats.views },
    { key: 'profileLinks.stats.taps', value: stats.appTaps + stats.waTaps },
    { key: 'profileLinks.stats.people', value: stats.contacts },
    { key: 'profileLinks.stats.joined', value: stats.signups },
  ];
}

// ---- the editor ----

export const EDITOR_TABS = ['card', 'ai', 'design', 'settings', 'people'] as const;
export type EditorTab = (typeof EDITOR_TABS)[number];

/** `?tab=` → a tab; anything else is the Card tab. */
export function parseEditorTab(value: unknown): EditorTab {
  return typeof value === 'string' && (EDITOR_TABS as readonly string[]).includes(value) ? (value as EditorTab) : 'card';
}

/**
 * The editor's working copy. Lists are edited as one line per item, which is what the owner types; the
 * palette is the accent only (D8: the page is one fixed Symphony template, the owner picks its colour).
 */
export interface EditorDraft {
  contextTitle: string;
  headline: string;
  bio: string;
  topics: string;
  greeting: string;
  suggestedAsks: string;
  instructions: string;
  allowedActions: ProfileLinkAction[];
  accent: string;
  /** `YYYY-MM-DD`, or '' for never. Always '' on the main profile (D7). */
  expiresOn: string;
  /** Credits per day as typed; '' or '0' = no cap. */
  dailyCreditCap: string;
}

/* EXEMPT — the swatches are DATA an owner stores (their page's accent), written into the public page as
   custom properties, not app styling. The first is the Symphony brand green, --ds-fill-accent-brand-default. */
export const PALETTE_SWATCHES: readonly string[] = ['#a4ff70', '#8fb8ff', '#ffb199', '#c9b6ff'];
/* end EXEMPT */

/** One item per line; blank lines dropped; each trimmed. Duplicates are kept — the owner may mean them. */
export function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

const joinLines = (items: readonly string[]) => items.join('\n');

export function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : new Date(ms).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` → the END of that day in UTC, so a link "expires on Nov 30" still works on Nov 30. */
export function dateInputToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const ms = Date.parse(`${value}T23:59:59.000Z`);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export function draftFromLink(link: ProfileLink): EditorDraft {
  return {
    contextTitle: link.contextTitle ?? '',
    headline: link.card.headline,
    bio: link.card.bio,
    topics: joinLines(link.card.topics),
    greeting: link.card.greeting,
    suggestedAsks: joinLines(link.card.suggestedAsks),
    instructions: link.instructions,
    allowedActions: [...link.allowedActions],
    accent: normalizePalette(link.card.design.palette).accent,
    expiresOn: link.contextSlug ? isoToDateInput(link.expiresAt) : '',
    dailyCreditCap: link.stats.dailyCreditCap > 0 ? String(link.stats.dailyCreditCap) : '',
  };
}

export type DraftErrors = Partial<Record<keyof EditorDraft, { key: string; params?: Record<string, number> }>>;

/** What blocks a save. Keys are i18n keys, so the editor renders them in the reader's language. */
export function validateDraft(draft: EditorDraft, link: Pick<ProfileLink, 'contextSlug'>): DraftErrors {
  const L = PROFILE_LINK_LIMITS;
  const errors: DraftErrors = {};
  const tooLong = (field: keyof EditorDraft, value: string, max: number) => {
    if (value.length > max) errors[field] = { key: 'profileLinks.error.tooLong', params: { max } };
  };
  if (link.contextSlug) {
    if (!draft.contextTitle.trim()) errors.contextTitle = { key: 'profileLinks.error.required' };
    else tooLong('contextTitle', draft.contextTitle, L.contextTitle);
  }
  tooLong('headline', draft.headline, L.headline);
  tooLong('bio', draft.bio, L.bio);
  tooLong('greeting', draft.greeting, L.greeting);
  tooLong('instructions', draft.instructions, L.instructions);
  const topics = splitLines(draft.topics);
  if (topics.length > L.topics) errors.topics = { key: 'profileLinks.error.tooMany', params: { max: L.topics } };
  else if (topics.some((t) => t.length > L.topic)) errors.topics = { key: 'profileLinks.error.itemTooLong', params: { max: L.topic } };
  const asks = splitLines(draft.suggestedAsks);
  if (asks.length > L.suggestedAsks) errors.suggestedAsks = { key: 'profileLinks.error.tooMany', params: { max: L.suggestedAsks } };
  else if (asks.some((a) => a.length > L.suggestedAsk)) errors.suggestedAsks = { key: 'profileLinks.error.itemTooLong', params: { max: L.suggestedAsk } };
  if (draft.expiresOn && !dateInputToIso(draft.expiresOn)) errors.expiresOn = { key: 'profileLinks.error.invalidDate' };
  if (draft.dailyCreditCap.trim()) {
    const cap = Number(draft.dailyCreditCap);
    if (!Number.isInteger(cap) || cap < 0 || cap > L.dailyCreditCap) errors.dailyCreditCap = { key: 'profileLinks.error.invalidCap' };
  }
  return errors;
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const sameActions = (a: readonly ProfileLinkAction[], b: readonly ProfileLinkAction[]) =>
  sameList([...a].sort(), [...b].sort());

/**
 * ONLY what changed, or null when nothing did. A write that restates unchanged values clobbers a concurrent
 * edit by the Maestro (`ManageProfileLinks update`, §8.1) — the same reason `routine-update.ts` sends a diff.
 */
export function patchFromDraft(link: ProfileLink, draft: EditorDraft): ProfileLinkPatch | null {
  const patch: ProfileLinkPatch = {};
  const card: NonNullable<ProfileLinkPatch['card']> = {};
  if (link.contextSlug && draft.contextTitle.trim() !== (link.contextTitle ?? '')) patch.contextTitle = draft.contextTitle.trim();
  if (draft.headline.trim() !== link.card.headline) card.headline = draft.headline.trim();
  if (draft.bio.trim() !== link.card.bio) card.bio = draft.bio.trim();
  if (draft.greeting.trim() !== link.card.greeting) card.greeting = draft.greeting.trim();
  const topics = splitLines(draft.topics);
  if (!sameList(topics, link.card.topics)) card.topics = topics;
  const asks = splitLines(draft.suggestedAsks);
  if (!sameList(asks, link.card.suggestedAsks)) card.suggestedAsks = asks;
  const palette = normalizePalette(link.card.design.palette);
  const accent = draft.accent.toLowerCase();
  if (accent !== palette.accent) card.design = { palette: normalizePalette({ ...palette, accent }) };
  if (Object.keys(card).length) patch.card = card;
  if (draft.instructions !== link.instructions) patch.instructions = draft.instructions;
  const actions = PROFILE_LINK_ACTIONS.filter((a) => draft.allowedActions.includes(a));
  if (!sameActions(actions, link.allowedActions)) patch.allowedActions = actions;
  if (link.contextSlug) {
    const expiresAt = draft.expiresOn ? dateInputToIso(draft.expiresOn) : null;
    if (isoToDateInput(expiresAt ?? undefined) !== isoToDateInput(link.expiresAt)) patch.expiresAt = expiresAt;
  }
  const cap = draft.dailyCreditCap.trim() ? Number(draft.dailyCreditCap) : 0;
  if (Number.isInteger(cap) && cap !== link.stats.dailyCreditCap) patch.dailyCreditCap = cap;
  return Object.keys(patch).length ? patch : null;
}

export function toggleAction(actions: readonly ProfileLinkAction[], action: ProfileLinkAction, on: boolean): ProfileLinkAction[] {
  const set = new Set(actions);
  if (on) set.add(action);
  else set.delete(action);
  return PROFILE_LINK_ACTIONS.filter((a) => set.has(a));
}

/** The "Your AI can" rows, in order, with their i18n keys. */
export const ACTION_ROWS: readonly { action: ProfileLinkAction; key: string }[] = [
  { action: 'MESSAGE', key: 'profileLinks.ai.action.message' },
  { action: 'REQUEST_FILE', key: 'profileLinks.ai.action.files' },
  { action: 'REQUEST_MEETING', key: 'profileLinks.ai.action.meetings' },
];

/**
 * What a visitor would see right now — the live preview beside the editor, rendered by the same
 * `ProfileLanding` the public page uses, so the preview cannot drift from the page.
 */
export function previewCard(link: ProfileLink, draft: EditorDraft, displayName: string): PublicProfileCard {
  const palette: ProfilePalette = normalizePalette({ ...link.card.design.palette, accent: draft.accent });
  return {
    handle: link.handle,
    contextSlug: link.contextSlug,
    contextTitle: link.contextSlug ? draft.contextTitle.trim() || link.contextSlug : undefined,
    displayName: displayName || link.handle,
    headline: draft.headline.trim(),
    bio: draft.bio.trim(),
    topics: splitLines(draft.topics),
    palette,
    // A preview's buttons go nowhere real: the short code is the backend's, and the page is not live yet.
    waShortCode: 'SY-PREVIEW',
    appLink: '',
    fellBackToMain: false,
  };
}

// ---- settings: the daily credit cap (Q2) ----

export interface CreditsLine {
  used: number;
  /** 0 = no cap. */
  cap: number;
  /** At or past the cap: the AI stops answering, messages still arrive (§11.2 B). */
  capped: boolean;
}

export function creditsLine(stats: ProfileLinkStats): CreditsLine {
  const cap = Math.max(0, stats.dailyCreditCap);
  return { used: stats.creditsToday, cap, capped: cap > 0 && stats.creditsToday >= cap };
}

// ---- leads ----

export function leadInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('');
}

/** Waiting-for-you first (the ones that need the owner), then newest first. */
export function sortLeads(leads: readonly ProfileLead[]): ProfileLead[] {
  return [...leads].sort((a, b) => {
    const waiting = Number(b.state === 'WAITING_FOR_YOU') - Number(a.state === 'WAITING_FOR_YOU');
    return waiting || Date.parse(b.at) - Date.parse(a.at);
  });
}

export const leadChannelKey = (lead: Pick<ProfileLead, 'channel'>) =>
  lead.channel === 'WHATSAPP' ? 'profileLinks.people.channel.whatsapp' : 'profileLinks.people.channel.app';

export const leadStateKey = (lead: Pick<ProfileLead, 'state'>) =>
  lead.state === 'WAITING_FOR_YOU' ? 'profileLinks.people.waiting' : 'profileLinks.people.aiAnswered';

// ---- the share panel ----

export const SHARE_PRESETS = ['symphony', 'owner', 'projector'] as const;
export type SharePreset = (typeof SHARE_PRESETS)[number];

export const SHARE_PRESET_KEYS: Record<SharePreset, string> = {
  symphony: 'profileLinks.share.preset.symphony',
  owner: 'profileLinks.share.preset.owner',
  projector: 'profileLinks.share.preset.plain',
};

/** A filename the owner will recognise in their Downloads: `symphony-ronny.elkayam-devconf26.svg`. */
export function qrFileName(link: Pick<ProfileLink, 'handle' | 'contextSlug'>, ext: 'svg' | 'png'): string {
  const slug = [link.handle, link.contextSlug].filter(Boolean).join('-').replace(/[^a-z0-9.-]/gi, '-');
  return `symphony-${slug}.${ext}`;
}

// ---- new context link ----

/** A title the owner typed → a URL slug (`DevConf 26` → `devconf-26`). Empty when nothing usable is left. */
export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

// ── Lead cards in People (§8.3) ──

/** Links whose leads People reads, at most — People is a summary; the link's own People tab is the full list. */
export const PEOPLE_LEAD_LINKS_MAX = 10;
/** Lead rows People shows at most. */
export const PEOPLE_LEADS_MAX = 20;

/** The links whose leads People reads: every link a visitor could ever have reached (a draft never could). */
export function peopleLeadLinks(links: readonly ProfileLink[]): ProfileLink[] {
  return links.filter((link) => link.status !== 'DRAFT').slice(0, PEOPLE_LEAD_LINKS_MAX);
}

/**
 * One lead as People shows it, with the link it came through: `linkPath` (`/me/<handle>[/<context>]`) and
 * `linkLabel` (its context title, else its slug; null = the main profile). The row reads
 * "<path> · <label or Main profile>", as iOS and Android `ProfileLinksLogic.leadSource` do.
 */
export interface PeopleLeadRow {
  key: string;
  lead: ProfileLead;
  linkId: string;
  linkPath: string;
  linkLabel: string | null;
}

/**
 * Every lead of every read link, as People's rows: newest first contact first, at most
 * {@link PEOPLE_LEADS_MAX}. A link whose leads are not loaded (yet, or at all) contributes nothing — the
 * section never waits on its slowest link.
 */
export function peopleLeadRows(
  links: readonly ProfileLink[],
  leadsByLink: Readonly<Record<string, readonly ProfileLead[] | undefined>>,
): PeopleLeadRow[] {
  const rows: PeopleLeadRow[] = [];
  for (const link of links) {
    for (const lead of leadsByLink[link.id] ?? []) {
      const slug = link.contextSlug?.trim();
      rows.push({
        key: `${link.id}:${lead.id}`,
        lead,
        linkId: link.id,
        linkPath: `/me/${link.handle}${slug ? `/${slug}` : ''}`,
        linkLabel: slug ? link.contextTitle?.trim() || slug : null,
      });
    }
  }
  const at = (row: PeopleLeadRow) => Date.parse(row.lead.at) || 0;
  return rows.sort((a, b) => at(b) - at(a)).slice(0, PEOPLE_LEADS_MAX);
}

/** Where tapping a lead goes: its owner-side conversation when there is one, else the link's People tab. */
export type LeadOpenTarget = { kind: 'conversation'; conversationId: string } | { kind: 'link'; linkId: string };

export function leadOpenTarget(row: Pick<PeopleLeadRow, 'lead' | 'linkId'>): LeadOpenTarget {
  const conversationId = row.lead.conversationId?.trim();
  return conversationId ? { kind: 'conversation', conversationId } : { kind: 'link', linkId: row.linkId };
}
